import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, catchError, of } from 'rxjs';
import { AppSettingsService } from './app-settings.service';
import { LoggerService } from './logger.service';

export interface StartDiagnosticSessionRequest {
  sourceSystem?: string;
  sourceEnvironment?: string;
  note?: string;
}

export interface StopDiagnosticSessionRequest {
  sessionId: string;
  note?: string;
}

export interface StartDiagnosticSessionResponse {
  sessionId?: string;
  SessionId?: string;
  success?: boolean;
  Success?: boolean;
  error?: string;
  Error?: string;
}

export interface StopDiagnosticSessionResponse {
  sessionId?: string;
  SessionId?: string;
  success?: boolean;
  Success?: boolean;
  error?: string;
  Error?: string;
}

export interface DiagnosticSessionState {
  isEnabled: boolean;
  isActive: boolean;
  sessionId: string | null;
  isBusy: boolean;
  error: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class DiagnosticsSessionService {
  private readonly sessionStorageKey = 'solo_diagnostic_session_id';
  private readonly sessionHeaderName = 'X-Codetorch-Diagnostics-Session';
  private readonly shortcutKey = 'd';

  private readonly _state = new BehaviorSubject<DiagnosticSessionState>({
    isEnabled: false,
    isActive: false,
    sessionId: null,
    isBusy: false,
    error: null
  });

  private isInitialized = false;
  private isShortcutRegistered = false;

  state$ = this._state.asObservable();

  constructor(
    private readonly http: HttpClient,
    private readonly appSettingsService: AppSettingsService,
    private readonly logger: LoggerService
  ) {
    const storedSessionId = this.getStoredSessionId();
    if (storedSessionId) {
      this._state.next({
        ...this._state.value,
        isActive: true,
        sessionId: storedSessionId
      });
    }

    this.registerShortcut();
    this.exposeWindowHelpers();

    (window as any).getDiagnosticSessionId = () => this.getActiveSessionId();
  }

  initialize(isEnabled = true): void {
    if (this.isInitialized) {
      this.setEnabled(isEnabled);
      return;
    }

    this.isInitialized = true;
    this.setEnabled(isEnabled);
  }

  get headerName(): string {
    return this.sessionHeaderName;
  }

  get isActive(): boolean {
    return this._state.value.isActive;
  }

  get isBusy(): boolean {
    return this._state.value.isBusy;
  }

  get activeSessionId(): string | null {
    return this._state.value.sessionId;
  }

  getActiveSessionId(): string | null {
    return this._state.value.sessionId;
  }

  setEnabled(enabled: boolean): void {
    this._state.next({
      ...this._state.value,
      isEnabled: enabled,
      isBusy: false,
      error: this._state.value.error
    });
  }

  async toggleCapture(): Promise<boolean> {
    if (this.isActive) {
      return this.stopCapture('Stopped from UI shortcut.');
    }

    return this.startCapture('Started from UI shortcut.');
  }

  async startCapture(reason?: string): Promise<boolean> {
    if (this._state.value.isBusy) {
      return false;
    }

    if (!this._state.value.isEnabled) {
      return false;
    }

    if (this._state.value.isActive) {
      return true;
    }

    this._state.next({
      ...this._state.value,
      isBusy: true,
      error: null
    });

    try {
      const settings = await this.appSettingsService.loadSettings();

      const request: StartDiagnosticSessionRequest = {
        sourceSystem: 'solo-web',
        sourceEnvironment: settings.environment,
        note: reason ?? 'Diagnostic capture started in Solo.'
      };

      const response = await firstValueFrom(
        this.http.post<StartDiagnosticSessionResponse>(
          `${settings.apiUrl}/api/diagnostics/session/start`,
          request
        ).pipe(
          catchError(error => {
            this.logger.error('DiagnosticsSessionService', 'startCapture', 'Failed to start diagnostic session', {
              error
            });

            return of({
              success: false,
              error: 'Unable to start diagnostic capture.'
            } as StartDiagnosticSessionResponse);
          })
        )
      );

      const normalizedResponse = this.normalizeResponse(response);
      if (!normalizedResponse.success || !normalizedResponse.sessionId) {
        this.logger.error('DiagnosticsSessionService', 'startCapture', 'Diagnostic start failed', {
          error: normalizedResponse.error
        });

        this._state.next({
          ...this._state.value,
          isBusy: false,
          error: normalizedResponse.error ?? 'Unable to start diagnostic capture.'
        });
        return false;
      }

      this.setSessionId(normalizedResponse.sessionId);

      this.logger.info('DiagnosticsSessionService', 'startCapture', 'Diagnostic capture started', {
        sessionId: normalizedResponse.sessionId
      });

      return true;
    } catch (error) {
      const message = 'Failed to start diagnostic capture.';
      this.logger.error('DiagnosticsSessionService', 'startCapture', message, { error });
      this._state.next({
        ...this._state.value,
        isBusy: false,
        error: message
      });
      return false;
    }
  }

  async stopCapture(reason?: string): Promise<boolean> {
    const sessionId = this._state.value.sessionId;
    if (!sessionId) {
      return false;
    }

    if (this._state.value.isBusy) {
      return false;
    }

    this._state.next({
      ...this._state.value,
      isBusy: true,
      error: null
    });

    try {
      const settings = await this.appSettingsService.loadSettings();
      const payload: StopDiagnosticSessionRequest = {
        sessionId,
        note: reason ?? 'Diagnostic capture stopped in Solo.'
      };

      const response = await firstValueFrom(
        this.http.post<StopDiagnosticSessionResponse>(
          `${settings.apiUrl}/api/diagnostics/session/stop`,
          payload
        ).pipe(
          catchError(error => {
            this.logger.error('DiagnosticsSessionService', 'stopCapture', 'Failed to stop diagnostic session', {
              error
            });

            return of({
              success: false,
              error: 'Unable to stop diagnostic capture.'
            } as StopDiagnosticSessionResponse);
          })
        )
      );

      const normalizedResponse = this.normalizeResponse(response);
      if (!normalizedResponse.success) {
        this.logger.warn('DiagnosticsSessionService', 'stopCapture', 'Diagnostic stop returned warning', {
          error: normalizedResponse.error,
          sessionId
        });

        this._state.next({
          ...this._state.value,
          isBusy: false,
          error: normalizedResponse.error
        });
        return false;
      }

      this.setSessionId(null);
      this.logger.info('DiagnosticsSessionService', 'stopCapture', 'Diagnostic capture stopped', { sessionId });
      return true;
    } catch (error) {
      const message = 'Failed to stop diagnostic capture.';
      this.logger.error('DiagnosticsSessionService', 'stopCapture', message, { error });
      this._state.next({
        ...this._state.value,
        isBusy: false,
        error: message
      });
      return false;
    }
  }

  private setSessionId(sessionId: string | null): void {
    const normalizedSessionId = sessionId?.trim();

    this.persistSessionId(normalizedSessionId);
    this._state.next({
      ...this._state.value,
      isBusy: false,
      error: null,
      isActive: !!normalizedSessionId,
      sessionId: normalizedSessionId ?? null
    });
  }

  private getStoredSessionId(): string | null {
    try {
      return localStorage.getItem(this.sessionStorageKey);
    } catch {
      return null;
    }
  }

  private persistSessionId(sessionId: string | null | undefined): void {
    try {
      if (sessionId && sessionId.length > 0) {
        localStorage.setItem(this.sessionStorageKey, sessionId);
      } else {
        localStorage.removeItem(this.sessionStorageKey);
      }
    } catch {
      // ignore storage failures
    }
  }

  private registerShortcut(): void {
    if (this.isShortcutRegistered) {
      return;
    }

    this.isShortcutRegistered = true;
    const eventHandler = async (event: KeyboardEvent) => {
      const normalizedKey = event.key?.toLowerCase();
      const usesCtrlShift = event.ctrlKey && event.shiftKey;
      const usesCtrlAlt = event.ctrlKey && event.altKey;
      const usesMetaShift = event.metaKey && event.shiftKey;
      const isSupportedShortcut = usesCtrlShift || usesCtrlAlt || usesMetaShift;

      if (!isSupportedShortcut || normalizedKey !== this.shortcutKey) {
        return;
      }

      event.preventDefault();
      await this.toggleCapture();
    };

    window.addEventListener('keydown', eventHandler);
  }

  private exposeWindowHelpers(): void {
    (window as any).startDiagnosticCapture = () => void this.startCapture('Started from browser console.');
    (window as any).stopDiagnosticCapture = () => void this.stopCapture('Stopped from browser console.');
    (window as any).toggleDiagnosticCapture = () => void this.toggleCapture();
  }

  private normalizeResponse(
    response?: StartDiagnosticSessionResponse | StopDiagnosticSessionResponse | null
  ): { success: boolean; sessionId: string | null; error: string | null } {
    return {
      success: response?.success ?? response?.Success ?? false,
      sessionId: response?.sessionId ?? response?.SessionId ?? null,
      error: response?.error ?? response?.Error ?? null
    };
  }
}
